import {Divider, LinearProgress, Link, Stack, Tooltip} from "@mui/material";
import {FC, useMemo} from "react";
import Box from "@mui/material/Box";
import {useTranslation} from "react-i18next";
import Typography from "@mui/material/Typography";
import {getLinearProgressColor, getTextColorOnRecommended} from "../../../util/utils";
import {IProfileWish, IWish, WishType} from "../../../store/wish/Helper";
import {RoutePaths} from "../../../util/constants";
import {useTheme} from "@mui/material/styles";
import {RootState, useAppSelector} from "../../../store";
import {computeSeededTicketsForWish} from "./TicketsCountLinearProgressHelper";

interface Props {
    wish: IWish | IProfileWish
    recommended: boolean
    accumulateTotals?: boolean
}

const TicketsCountLinearProgress: FC<Props> = ({accumulateTotals = false, ...props}) => {
    const {t} = useTranslation()
    const theme = useTheme()
    const serverTime = useAppSelector((state: RootState) => state.appReducer.serverTime);

    // List of wishes to display (parent only OR parent+children for totals)
    const wishes = useMemo(() => [props.wish, ...(props.wish.children ?? [])], [props.wish]);

    // // REAL totals (unchanged)
    // const current = useMemo(() =>
    //         accumulateTotals
    //             ? wishes.reduce((acc, w) => acc + w.tickets_count, 0)
    //             : props.wish.tickets_count
    //     , [accumulateTotals, wishes, props.wish.tickets_count]);

    const total = useMemo(() =>
            accumulateTotals
                ? wishes.reduce((acc, w) => acc + w.max_tickets, 0)
                : props.wish.max_tickets
        , [accumulateTotals, wishes, props.wish.max_tickets]);

    // SEEDED totals – sum per-wish seeded values so parent == sum(children)
    const displayedCurrent = useMemo(() => {
        const nowMs = serverTime ? new Date(serverTime).getTime() : Date.now();
        const list = accumulateTotals ? wishes : [props.wish];

        return list.reduce((acc, w) => acc + computeSeededTicketsForWish({
            id: w.id,
            actual: w.tickets_count,
            max: w.max_tickets,
            startISO: w.start_time,
            endISO: w.end_time,
            nowMs,
        }), 0);
    }, [accumulateTotals, wishes, props.wish, serverTime]);

    const color = getLinearProgressColor(displayedCurrent, total);

    const handleLinearProgressClick = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
    }
    const handleReadMoreClick = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
    }

    const getLabelColor = useMemo(() => getTextColorOnRecommended(props.recommended), [props.recommended])

    return (
        <Box>
            <Typography variant='subtitle1' sx={{color: getLabelColor}}>
                {props.wish.type === WishType.VOUCHER ? t('bought_vouchers') : t('obtained_entries')}:
            </Typography>
            <Tooltip
                enterTouchDelay={0}
                title={
                    <Box textAlign='center'>
                        <Stack spacing={2}
                               divider={<Divider sx={{backgroundColor: theme.palette.primary.main}}
                                                 orientation="vertical" flexItem/>}
                               direction="row">
                            <Box textAlign="center">
                                <Typography variant="subtitle2" fontWeight={600}>
                                    {props.wish.type === WishType.VOUCHER ? t('bought') : t('obtained')}
                                </Typography>
                                <Typography variant="subtitle2">{displayedCurrent}</Typography>
                            </Box>
                            <Box textAlign="center">
                                <Typography variant="subtitle2" fontWeight={600}>{t('available')}</Typography>
                                <Typography variant="subtitle2">{Math.max(total - displayedCurrent, 0)}</Typography>
                            </Box>
                        </Stack>
                        <Link color="primary" href={RoutePaths.GUIDE} target="_blank" rel="noopener noreferrer"
                              sx={{mt: 0.5}} onClick={handleReadMoreClick}>
                            ({t('read_more')})
                        </Link>
                    </Box>
                }
                arrow placement="top"
            >
                <LinearProgress
                    variant='determinate'
                    color={color}
                    value={total > 0 ? Math.round((displayedCurrent / total) * 100) : 0}
                    onMouseDown={handleLinearProgressClick}
                    onTouchStart={handleLinearProgressClick}
                    onClick={handleLinearProgressClick}
                    sx={{height: "14px", borderRadius: "3px"}}
                />
            </Tooltip>
        </Box>
    )
}

export default TicketsCountLinearProgress
